/**
 * Invoice line-item column data types.
 *
 * Official defaults live here (same pattern as AI Calling catalog).
 * ATS `GET /invoice/config` and template `lineItemColumns` can replace or
 * extend the allowed list. A stored type is never rewritten to `text` /
 * `number` just because it is missing from a JS enum.
 */

import { normalizeColumnType } from '@/utils/invoice-calculation-engine'

export const SYSTEM_COLUMN_TYPES = Object.freeze(['move', 'actions'])

export const OFFICIAL_LINE_ITEM_COLUMN_TYPES = Object.freeze([
  { value: 'text', text: 'Text' },
  { value: 'number', text: 'Number' },
  { value: 'currency', text: 'Currency' },
  { value: 'percentage', text: 'Percentage' },
  { value: 'date', text: 'Date' },
  { value: 'sac-select', text: 'SAC Select' },
  { value: 'title-select', text: 'Title Select' },
  { value: 'computed', text: 'Computed' },
])

function emptyOverride() {
  return {
    types: null,
    defaultType: null,
    labels: null,
  }
}

let override = emptyOverride()
const observedTypes = new Map()

function clone(value) {
  if (value == null) return value
  if (typeof structuredClone === 'function') return structuredClone(value)
  return JSON.parse(JSON.stringify(value))
}

function unwrapConfig(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  let src = raw
  if (src.data && typeof src.data === 'object' && !Array.isArray(src.data)
    && (src.data.lineItemColumnTypes || src.data.columnTypes || src.data.dataTypes
      || src.data.validationRules || src.data.lineItemTypes)) {
    src = src.data
  }
  if (src.validationRules && typeof src.validationRules === 'object' && !Array.isArray(src.validationRules)) {
    src = { ...src, ...src.validationRules }
  }
  return src
}

function humanizeType(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  return raw
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
}

export function isSystemColumnType(type) {
  return SYSTEM_COLUMN_TYPES.includes(normalizeColumnType(type))
}

function officialLabel(value) {
  const found = OFFICIAL_LINE_ITEM_COLUMN_TYPES.find((row) => row.value === value)
  return found ? found.text : ''
}

export function getColumnTypeLabel(type) {
  const value = normalizeColumnType(type)
  if (!value) return ''
  if (override.labels && override.labels[value]) return override.labels[value]
  const fromCatalog = override.types && override.types.find((row) => row.value === value)
  if (fromCatalog && fromCatalog.text) return fromCatalog.text
  if (observedTypes.has(value) && observedTypes.get(value)) return observedTypes.get(value)
  return officialLabel(value) || humanizeType(type) || value
}

function normalizeTypeOption(item) {
  if (typeof item === 'string') {
    const value = normalizeColumnType(item)
    if (!value || isSystemColumnType(value)) return null
    return { value, text: getColumnTypeLabel(value) || humanizeType(item) || value }
  }
  if (!item || typeof item !== 'object') return null
  const value = normalizeColumnType(item.value || item.type || item.key || item.id || item.data_type)
  if (!value || isSystemColumnType(value)) return null
  const text = String(item.text || item.label || item.name || '').trim()
    || getColumnTypeLabel(value)
    || humanizeType(value)
    || value
  return { value, text }
}

function asTypeList(value) {
  if (!value) return null
  if (Array.isArray(value) && value.length) return value
  if (typeof value === 'object' && !Array.isArray(value)) {
    const keys = Object.keys(value)
    if (!keys.length) return null
    return keys.map((key) => {
      const row = value[key]
      if (row && typeof row === 'object') return { value: row.value || row.type || key, ...row }
      return { value: key, text: row }
    })
  }
  return null
}

export function observeLineItemColumnTypes(columns) {
  if (!Array.isArray(columns)) return
  columns.forEach((col) => {
    if (!col || typeof col !== 'object') return
    const value = normalizeColumnType(col.type || col.data_type || col.dataType)
    if (!value || isSystemColumnType(value)) return
    const label = String(col.typeLabel || col.type_label || '').trim()
      || officialLabel(value)
      || humanizeType(col.type)
      || value
    if (!observedTypes.has(value)) observedTypes.set(value, label)
  })
}

export function observeTemplateColumnTypes(templates) {
  const rows = Array.isArray(templates) ? templates : (templates ? [templates] : [])
  rows.forEach((row) => {
    const config = (row && row.config && typeof row.config === 'object') ? row.config : row
    const cols = (config && config.lineItemColumns) || (row && row.lineItemColumns) || []
    observeLineItemColumnTypes(cols)
  })
}

export function applyInvoiceColumnTypeCatalog(config) {
  const src = unwrapConfig(config)
  if (!src || typeof src !== 'object') return getInvoiceColumnTypeCatalogSnapshot()

  const list = asTypeList(
    src.lineItemColumnTypes
    || src.columnTypes
    || src.dataTypes
    || src.allowedColumnTypes
    || src.lineItemTypes
    || src.line_item_column_types
    || src.column_types,
  )
  if (list) {
    const rows = list.map(normalizeTypeOption).filter(Boolean)
    if (rows.length) override.types = rows
  }

  const defaultRaw = src.defaultColumnType || src.defaultLineItemColumnType
    || src.defaultType || src.default_column_type
  if (defaultRaw != null && String(defaultRaw).trim() !== '') {
    override.defaultType = normalizeColumnType(defaultRaw) || String(defaultRaw).trim()
  }

  const labels = src.columnTypeLabels || src.typeLabels || src.column_type_labels
  if (labels && typeof labels === 'object' && !Array.isArray(labels)) {
    override.labels = {}
    Object.keys(labels).forEach((key) => {
      const value = normalizeColumnType(key)
      if (value) override.labels[value] = String(labels[key] || '').trim() || humanizeType(key)
    })
  }

  observeTemplateColumnTypes(src.templates || src.invoiceTemplates)
  observeLineItemColumnTypes(src.lineItemColumns)

  return getInvoiceColumnTypeCatalogSnapshot()
}

export function getDefaultColumnType() {
  const configured = normalizeColumnType(override.defaultType)
  if (configured) return configured
  const first = (override.types && override.types[0] && override.types[0].value) || ''
  return first || 'text'
}

/**
 * Honor a stored type. Only when the value is blank do we use a catalog
 * default — unknown types stay unknown (never coerced to text/number).
 */
export function resolveColumnType(type, emptyFallback) {
  const stored = normalizeColumnType(type)
  if (stored) return stored
  const fallback = normalizeColumnType(emptyFallback)
  if (fallback) return fallback
  return getDefaultColumnType()
}

export function getLineItemColumnTypeOptions(extraTypes) {
  // Official types always stay available. Prod GET /invoice/config sometimes
  // returns a catalog without `computed` — that used to hide the Computed
  // option and skip formula evaluation after a type round-trip.
  const seen = new Set()
  const base = []
  const add = (opt) => {
    if (!opt || !opt.value || seen.has(opt.value) || isSystemColumnType(opt.value)) return
    seen.add(opt.value)
    base.push({ value: opt.value, text: opt.text || getColumnTypeLabel(opt.value) })
  }
  OFFICIAL_LINE_ITEM_COLUMN_TYPES.forEach((row) => add({ value: row.value, text: row.text }))
  ;(override.types || []).forEach((row) => add({ value: row.value, text: row.text }))

  const extras = []
  observedTypes.forEach((text, value) => extras.push({ value, text }))
  if (Array.isArray(extraTypes)) {
    extraTypes.forEach((item) => {
      const opt = normalizeTypeOption(item)
      if (opt) extras.push(opt)
    })
  }
  extras.forEach((opt) => add(opt))
  return base
}

export function getInvoiceColumnTypeCatalogSnapshot() {
  return {
    types: clone(override.types),
    defaultType: override.defaultType,
    observed: Array.from(observedTypes.keys()),
    options: getLineItemColumnTypeOptions(),
  }
}

export function resetInvoiceColumnTypeCatalog() {
  override = emptyOverride()
  observedTypes.clear()
}
